import path from "constants/path";
import useSaveBookmarkModal from "features/Bookmarks/hooks/useSaveBookmarkModal";
import useThreads from "features/Sidebar/hooks/useThreads";
import useIncognitoMode from "features/User/hooks/useIncognitoMode";
import { MixpanelActionSource } from "helpers/services/mixpanel/events";
import useAnalytics from "hooks/useAnalytics";
import { useRouter } from "next/router";
import { HistoryItem, HistoryType } from "../types";
import { getSearchPathFromSearchHistory } from "../utils/histories";
import useSearchHistory from "./useSearchHistory";

function useHistoryActions() {
  const router = useRouter();
  const { isIncognitoModeActive } = useIncognitoMode();
  const { setAnalyticActionSource } = useAnalytics();
  const { openSaveBookmarkModalForSearch, openSaveBookmarkModalForThread } =
    useSaveBookmarkModal();

  const { deleteSearchHistory } = useSearchHistory();
  const { deleteThread } = useThreads(true);

  const handleClickHistory = async (
    history: HistoryItem,
    onNavigate?: () => void
  ) => {
    // Don't navigate if incognito mode is active
    if (isIncognitoModeActive) {
      return;
    }

    if (history.type === HistoryType.Search) {
      // Delete the search history, as clicking on it will subsequently convert this search into a
      // thread via the /results/ page and we don't want the history to be duplicated.
      await deleteSearchHistory(history.id);
    }

    onNavigate?.();
  };

  const handleSaveHistory = (history: HistoryItem) => {
    setAnalyticActionSource(MixpanelActionSource.Sidebar);

    if (history.type === HistoryType.Search) {
      const searchUrl = getSearchPathFromSearchHistory(history);
      openSaveBookmarkModalForSearch(searchUrl);
    } else if (history.type === HistoryType.Thread) {
      openSaveBookmarkModalForThread(history.thread_id);
    }
  };

  const handleDeleteHistory = async (history: HistoryItem) => {
    if (history.type === HistoryType.Search) {
      return await deleteSearchHistory(history.id);
    } else if (history.type === HistoryType.Thread) {
      await deleteThread(history.thread_id);

      // Check if the user is currently viewing the thread being deleted
      // Thread URLs follow the pattern /search/{slug}/{threadId}/
      const currentPath = router.asPath;
      const threadPattern = new RegExp(`/search/.+/${history.thread_id}/?`);

      if (threadPattern.test(currentPath)) {
        // Redirect to homepage if deleting the current thread
        router.push(path.HOME);
      }
    }
  };

  return {
    handleClickHistory,
    handleSaveHistory,
    handleDeleteHistory,
  };
}

export default useHistoryActions;
